import React, { useState, useEffect } from "react";

import Fuse from "fuse.js";
import { Label } from "@components/Catalyst/fieldset";
import { Switch } from "@components/Catalyst/switch";

import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions, Dialog, DialogPanel, DialogBackdrop } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { Brand } from "@typed/categories";
import { BrandCode } from "@typed/common";

type QuickSearchOption = {
  id: number;
  name: string;
  additionalSearchTerms?: string[];
  brand?: BrandCode[];
  url: string;
};

const useDebouncedValue = (inputValue: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue, delay]);

  return debouncedValue;
};

const NavSearchDialog = ({
  brand,
  searchOpen,
  setSearchOpen,
}: {
  brand?: Brand;
  searchOpen: boolean;
  setSearchOpen: (val: boolean) => void;
}) => {
  if (!process.env.GATSBY_ASSET_URL) throw new Error("GATSBY_ASSET_URL is not set in .env file");

  const ASSET_URL = process.env.GATSBY_ASSET_URL || "";
  const [searchValue, setSearchValue] = useState("");

  const [searchBrand, setSearchBrand] = useState<BrandCode>(() => (brand?.code as BrandCode) ?? "gac");

  const [fuse, setFuse] = useState<Fuse<QuickSearchOption> | null>(null);

  const [searchResults, setSearchResults] = useState<QuickSearchOption[]>([]);

  useEffect(() => {
    const fetchFuseIndex = async () => {
      const asset = `${ASSET_URL}/fuseIndex/fuseIndex.json`;
      const response = await fetch(asset);
      if (!response.ok) return;

      const fuseIndex = (await response.json()) as { docs: QuickSearchOption[]; index: string };
      if (!fuseIndex) return;

      const lFuse = new Fuse([...fuseIndex.docs], { minMatchCharLength: 2, threshold: 0.3 }, Fuse.parseIndex(fuseIndex.index));

      setFuse(lFuse);
    };

    // Delay execution so as not  to block render
    const timeout = setTimeout(() => {
      fetchFuseIndex();
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  const debouncedSearchValue = useDebouncedValue(searchValue, 200);

  useEffect(() => {
    if (!fuse || !debouncedSearchValue) {
      setSearchResults([]);
      return;
    }

    const filteredQuickSearchOptions = [
      ...fuse
        .search(debouncedSearchValue)
        .filter(({ item }) => searchBrand === "gac" || !item.brand || item.brand?.includes(searchBrand))
        .reduce((acc, { item }) => {
          acc.set(item.id, item);
          return acc;
        }, new Map<number, QuickSearchOption>())
        .values(),
    ];

    setSearchResults(filteredQuickSearchOptions);
  }, [debouncedSearchValue]);

  const fullSearchOption: QuickSearchOption = {
    id: -1,
    name: `Search for: '${searchValue}'`,
    url: `/search?query=${searchValue.replace(" ", "+")}`,
  };

  return (
    <Dialog
      className="relative z-10"
      open={searchOpen}
      onClose={() => {
        setSearchOpen(false);
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/5 transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <Combobox
            onChange={(quickSearch: QuickSearchOption) => {
              if (quickSearch) {
                if ((window as any).plausible)
                  (window as any).plausible("Search", { props: { searchTerm: searchValue, quickNav: quickSearch.name } });

                window.location.href = quickSearch.url;
              }
            }}
          >
            <div className="grid grid-cols-1">
              <ComboboxInput
                autoFocus
                autoComplete="off"
                className="col-start-1 row-start-1 h-12 w-full pl-11 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 sm:text-sm"
                placeholder="Search..."
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-400"
                aria-hidden="true"
              />

              {brand && brand?.code !== "gac" && (
                <div className="flex h-12 gap-2 pl-4 pr-4">
                  <Switch
                    checked={searchBrand === (brand?.code as BrandCode)}
                    onChange={(checked: boolean) => setSearchBrand(checked ? (brand?.code as BrandCode) : "gac")}
                  />

                  <Label className="text-gray-900">{`Show only ${brand.name} results`}</Label>
                </div>
              )}
            </div>

            {searchValue !== "" && (
              <ComboboxOptions static as="ul" className="max-h-80 scroll-py-2 divide-y divide-gray-100 overflow-y-auto">
                <li className="p-2">
                  <ul className="text-sm text-gray-700">
                    <ComboboxOption
                      key="full-search"
                      className="flex cursor-default select-none gap-2 px-4 py-2 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none"
                      value={fullSearchOption}
                    >
                      <MagnifyingGlassIcon
                        className="pointer-events-none col-start-1 row-start-1 size-5 self-center text-gray-400"
                        aria-hidden="true"
                      />
                      {`Search for: '${searchValue}'`}
                    </ComboboxOption>
                  </ul>
                </li>

                <li className="p-2">
                  <h2 className="mb-2 mt-4 px-3 text-xs font-semibold text-gray-500">Quick links</h2>

                  <ul className="text-sm text-gray-700">
                    {searchResults.length > 0 ? (
                      <>
                        {searchResults.map((qso) => (
                          <ComboboxOption
                            key={qso.id}
                            value={qso}
                            className="cursor-default select-none px-4 py-2 data-[focus]:bg-indigo-600 data-[focus]:text-white data-[focus]:outline-none"
                          >
                            {`${qso.url.includes("/news/") ? "News Article: " : ""}${qso.name}`}
                          </ComboboxOption>
                        ))}
                      </>
                    ) : (
                      <li className="p-4 text-sm text-gray-500">No quick links found.</li>
                    )}
                  </ul>
                </li>
              </ComboboxOptions>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default NavSearchDialog;
